import { Component, type ComponentType, type ErrorInfo, type ReactNode, Suspense } from 'react';
import { SkeletonCard } from './kit';
import type { Lazy, SlotName, SlotProps } from './module';
import { lazyPart, profileSections, slotEntries } from './registry';

/**
 * Renders one lazy module part (a Home card, a Me section, a slot entry) inside Suspense and
 * an error boundary, so one broken module never takes the screen down with it.
 */
class PartBoundary extends Component<{ children: ReactNode; name: string }, { failed: boolean }> {
  override state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  override componentDidCatch(error: unknown, info: ErrorInfo) {
    console.error(`[part ${this.props.name}] crashed`, error, info.componentStack);
  }
  override render() {
    return this.state.failed ? null : this.props.children;
  }
}

export function Part<P extends object>({
  loader,
  name,
  props,
  fallback,
}: {
  loader: Lazy<P>;
  name: string;
  props?: P;
  fallback?: ReactNode;
}) {
  const C = lazyPart(loader) as unknown as ComponentType<P>;
  return (
    <PartBoundary name={name}>
      <Suspense fallback={fallback ?? <SkeletonCard />}>
        <C {...((props ?? {}) as P)} />
      </Suspense>
    </PartBoundary>
  );
}

/**
 * A named extension point. Screens render `<Slot name="h2h.extra" a={a} b={b} />` and every
 * module that registered that slot draws its part there, in `order`.
 */
export function Slot<N extends SlotName>(props: { name: N } & SlotProps[N]) {
  const { name, ...rest } = props;
  const entries = slotEntries(name);
  if (entries.length === 0) return null;
  return (
    <>
      {entries.map((e) => (
        <Part
          key={e.module}
          name={`${e.module}:${name}`}
          loader={e.component as Lazy<object>}
          props={rest as object}
          fallback={null}
        />
      ))}
    </>
  );
}

/**
 * The player profile's module sections (sessions adds attendance, gamelab a Lab strip).
 * The players module renders this on /p/:id; the shell owns the composition.
 */
export function ProfileSections({ playerId }: { playerId: string }) {
  return (
    <>
      {profileSections().map((s) => (
        <section key={`${s.module}:${s.id}`} className="flex flex-col gap-2">
          {s.title && <h2 className="tts-display text-xl">{s.title}</h2>}
          <Part name={`${s.module}:${s.id}`} loader={s.component} props={{ playerId }} />
        </section>
      ))}
    </>
  );
}
